/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

const parseAddressInt = (address: string): number => {
    if (address.startsWith('0x')) {
        return parseInt(address, 16);
    }
    return parseInt(address, 10);
};

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    // modbus_master_request(1, ReadCoilStatus, , 1, "", MODBUS_MASTER_INTERFACE, modbus_TCP)
    const { project } = feature;
    const ID = feature.getID();
    const DATATYPE = feature.getSettingValue('DATATYPE');
    let ADDRESS = project.parseCodeExpression(feature.getSettingValue('ADDRESS'), [], TibboDataType.WORD);
    const NAME = feature.getSettingValue('NAME');
    const LENGTH = feature.getSettingValue('LENGTH');
    const DEVICE_INTERFACE = 'current_interface';
    const VARTYPE = feature.getSettingValue('VARTYPE');
    const ENDIAN = feature.getSettingValue('ENDIAN');
    const MULTIPLIER = feature.getSettingValue('MULTIPLIER');
    let INTERFACE = 'MODBUS_MASTER_INTERFACE';
    let device;
    let DEVICEID;
    let DEVICENAME;
    ADDRESS = parseAddressInt(ADDRESS);
    const devices = project.getFeature('modbus_master').getSettingValue('MODBUSDEVICES');
    for (let i = 0; i < devices.length; i += 1) {
        const registers = devices[i].getSettingValue('REGISTERS');
        for (let j = 0; j < registers.length; j += 1) {
            if (registers[j].getID() === ID) {
                device = devices[i];
                const PROTOCOL = device.getSettingValue('PROTOCOL');
                DEVICEID = `${device.getSettingValue('NAME')}_DEVICE_ID`;
                DEVICENAME = device.getSettingValue('NAME');
                let updateBlockCode = '';
                let pollLength = 0;
                let startRegister = ADDRESS;
                // see which register to start polling from if registers are sequential
                for (let k = 0; k < registers.length; k += 1) {
                    startRegister = parseAddressInt(registers[k].getSettingValue('ADDRESS'));
                    const registerType = registers[k].getSettingValue('DATATYPE');
                    const cLength = parseInt(registers[k].getSettingValue('LENGTH'), 10);
                    if (registerType !== DATATYPE) {
                        continue;
                    }
                    let kPollLength = cLength;
                    let currentRegister = startRegister;
                    for (let l = k + 1; l < registers.length; l += 1) {
                        const lStartRegister = parseAddressInt(registers[l].getSettingValue('ADDRESS'));
                        const prevLength = parseInt(registers[l - 1].getSettingValue('LENGTH'), 10);
                        if (currentRegister + prevLength === lStartRegister && registerType === registers[l].getSettingValue('DATATYPE')) {
                            currentRegister = lStartRegister;
                            kPollLength += prevLength;
                        } else {
                            break;
                        }
                    }
                    if (startRegister + kPollLength > ADDRESS && startRegister <= ADDRESS) {
                        pollLength = kPollLength;
                        break;
                    }
                }

                if (pollLength > 0 && startRegister === ADDRESS) {
                    if (PROTOCOL === 'modbus_TCP') {
                        INTERFACE = `${DEVICENAME}_MODBUS_SOCK_NUM`;
                        updateBlockCode = `
if interface_ready(current_interface) AND modbus_request_exists(${DATATYPE}, ${DEVICEID}, ${startRegister}, ${pollLength}, ${INTERFACE}, ${PROTOCOL})=NO then
    modbus_master_enqueue_request(${DEVICEID}, ${DATATYPE}, ${startRegister}, ${pollLength}, "", ${INTERFACE}, ${PROTOCOL})
end if`;
                    } else {
                        const feature = project.getFeature(device.getSettingValue('SERIAL_PORT'));
                        if (feature) {
                            INTERFACE = `SER_${feature.getSettingValue('NAME')}`;
                        }
                        updateBlockCode = `
if modbus_request_exists(${DATATYPE}, ${DEVICEID}, ${startRegister}, ${pollLength}, ${INTERFACE}, ${PROTOCOL})=NO then
    modbus_master_enqueue_request(${DEVICEID}, ${DATATYPE}, ${startRegister}, ${pollLength}, "", ${INTERFACE}, ${PROTOCOL})
end if`;
                    }
                }
                blocks.push({
                    location: `var_${NAME}_update`,
                    blockType: BlockType.CODE,
                    template: updateBlockCode,
                });

                if (DATATYPE === 'ReadCoilStatus') {
                    blocks.push({
                        location: `var_${NAME}_set`,
                        blockType: BlockType.CODE,
                        template: `
    dim coil_value as string=chr(0)+chr(0)
    if value<>0 then
        coil_value=chr(255)+chr(0)
    end if
    modbus_master_enqueue_request(${DEVICEID}, WriteSingleCoil, ${ADDRESS}, 1, coil_value, ${INTERFACE}, ${PROTOCOL})`,
                    });
                } else if (DATATYPE === 'ReadHoldingRegisters') {
                    let conversionCode = `
    dim register_data as string=""`;
                    if (Number(LENGTH) === 1) {
                        conversionCode += `
    dim tmp_value as word=value
    register_data=chr(tmp_value SHR 8)+chr(tmp_value AND &hFF)`;
                    } else if (Number(LENGTH) === 2) {
                        conversionCode += `
    dim tmp_value as dword=value
    register_data=chr(tmp_value SHR 24 AND &hFF)+chr(tmp_value SHR 16 AND &hFF)+chr(tmp_value SHR 8 AND &hFF)+chr(tmp_value AND &hFF)`;
                    }
                    blocks.push({
                        location: `var_${NAME}_set`,
                        blockType: BlockType.CODE,
                        template: `
    ${conversionCode}
    modbus_master_enqueue_request(${DEVICEID}, PresetMultipleRegisters, ${ADDRESS}, ${LENGTH}, register_data, ${INTERFACE}, ${PROTOCOL})
                                `,
                    });
                }
                const offsetCode = `
    dim address_offset as word=(${ADDRESS} - request_item.start_register) * (${LENGTH} * 2)
                `;

                let valueUpdateCompletedCode = '';

                let dataType = 'long';
                let readCode = '0+((bytes(address_offset) SHL 8) OR bytes(address_offset + 1))';
                let signedMax = 32767;
                let signedSubtract = 65536;
                if (Number(LENGTH) === 2) {
                    signedMax = 2147483647;
                    signedSubtract = 4294967295;
                }
                if (Number(LENGTH) === 2 && ENDIAN === 'BIG') { // 32 bit big endian
                    dataType = 'dword';
                    readCode = '(bytes(address_offset) SHL 24 AND &hFF000000) OR (bytes(address_offset + 1) SHL 16 AND &h00FF0000) OR (bytes(address_offset + 2) SHL 8 AND &h0000FF00) OR (bytes(address_offset + 3) AND &h000000FF)';
                } else if (Number(LENGTH) === 2 && ENDIAN === 'LITTLE') { // 32 bit little endian
                    dataType = 'dword';
                    readCode = '(bytes(address_offset) SHL 24 AND &hFF000000) OR (bytes(address_offset + 1) SHL 16 AND &h00FF0000) OR (bytes(address_offset + 2) SHL 8 AND &h0000FF00) OR (bytes(address_offset + 3) AND &h000000FF)';
                }

                if (DATATYPE === 'ReadCoilStatus' || DATATYPE === 'ReadInputStatus') {
                    readCode = 'bytes(address_offset) AND 1';
                }

                if ((DATATYPE === 'ReadHoldingRegisters' || DATATYPE === 'ReadInputRegisters')
                    && VARTYPE === 'SIGNED_INTEGER') {
                    valueUpdateCompletedCode = `
            dim regvalue as ${dataType} = ${readCode}
            if regvalue>${signedMax} then
                regvalue=regvalue-${signedSubtract}
            end if
            `;
                } else {
                    valueUpdateCompletedCode = `
            dim regvalue as ${dataType} = ${readCode}
            
                        `;
                }
                if (VARTYPE === 'FLOAT') {
                    valueUpdateCompletedCode = `
            dim regvalue as float = 0
            `;
                    if (ENDIAN === 'BIG') {
                        valueUpdateCompletedCode += `
            dim tmp_fstr as string(4) = mid(reply,address_offset+1,1) + mid(reply,address_offset+2,1) + mid(reply,address_offset+3,1) + mid(reply,address_offset+4,1)
            dim tmp_fstr_hex as string(8) = str_to_hex(tmp_fstr)
            `;
                    } else {
                        valueUpdateCompletedCode += `
            dim tmp_fstr as string(4) = mid(reply,address_offset+3,1) + mid(reply,address_offset+4,1) + mid(reply,address_offset+1,1) + mid(reply,address_offset+2,1)
            dim tmp_fstr_hex as string(8) = str_to_hex(tmp_fstr)
            `;
                    }
                    valueUpdateCompletedCode += `
            regvalue=math_hex_to_float(tmp_fstr_hex)
            `;
                }

                valueUpdateCompletedCode += `
var_${NAME}_update_completed(regvalue * ${MULTIPLIER})
data=regvalue`;

                blocks.push({
                    location: `${DEVICENAME}_DEVICE_ID`,
                    blockType: BlockType.CODE,
                    template: `
        if request_item.start_register <= ${ADDRESS} 
        AND request_item.start_register + request_item.count > ${ADDRESS} 
        AND request_item.request_function=${DATATYPE} then
            ${offsetCode}
            ${valueUpdateCompletedCode}
        end if`,
                });
                break;
            }
        }
    }

    return blocks;
};
